import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { OrderListItemDTO } from '@wf/shared'
import { initials } from '../app/initials'
import { formatDurationShort } from '../time/format'
import { scheduleFact } from './schedule'
import { localYmd } from './workshop'

// Status → Karten-Modifier (farbiger border-left, wie .wf-card--* im Alt-Monolithen).
const STATUS_MODIFIER: Record<string, string> = {
  offen: 'wf-card--offen',
  arbeit: 'wf-card--arbeit',
  wartet: 'wf-card--wartet',
  fertig: 'wf-card--fertig',
  abgeholt: 'wf-card--abgeholt',
}

const PRIO_LABEL: Record<string, string> = { hoch: 'HOCH', mittel: 'MITTEL', niedrig: 'NIEDRIG' }

/** Kompaktes 14px-Icon. SVG-Präsentationsattribute (kein style=) — bleibt unter der harten CSP zulässig. */
function Icon({ children, sw = 1.8 }: { children: ReactNode; sw?: number }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

/**
 * Auftragskarte im vollen v6-.wf-card-Look. Befüllt aus OrderListItemDTO — den denormalisierten
 * Anzeigefeldern, die das Backend pro Listen-Zeile mitliefert (kein N+1): Kennzeichen, Fahrzeug, Kunde,
 * Aufgaben-Fortschritt, gebuchte Zeit, Teile-/Positions-Zähler, Mechaniker.
 *
 * Telefon steht hier bewusst als Text, NICHT als tel:-Link: die ganze Karte ist ein <Link> aufs Detail, und
 * ein <a> im <a> wäre ungültiges Markup. Der Anruf-Link lebt im Detail.
 */
export function OrderCard({ order }: { order: OrderListItemDTO }) {
  const modifier = STATUS_MODIFIER[order.status] ?? ''
  const prio = order.prio || 'mittel'
  const abgeholt = order.status === 'abgeholt'
  const now = new Date()
  const today = localYmd(now)
  const tomorrow = localYmd(new Date(now.getTime() + 86400000))
  const overdue = order.scheduledDate != null && order.scheduledDate < today && !['fertig', 'abgeholt'].includes(order.status)
  const schedule = scheduleFact(order.scheduledDate, order.startMin, order.endMin, today, tomorrow)
  const valueCents = order.partsCents + order.itemsCents
  const showPaid = (order.status === 'fertig' || order.status === 'abgeholt') && valueCents > 0

  return (
    <Link to={`/orders/${order.id}`} className={`wf-card ${modifier}`.trim()} aria-label={`Auftrag ${order.nr ?? order.id}`}>
      <div className="wf-card__row">
        <span className="wf-plate">
          <span className="wf-plate__eu">D</span>
          <span className="wf-plate__nr">{order.plate ?? '—'}</span>
        </span>
        {abgeholt ? (
          <span className="wf-card__tag">ABGEHOLT</span>
        ) : (
          <span className={`wf-prio wf-prio--${prio}`}>{PRIO_LABEL[prio] ?? prio.toUpperCase()}</span>
        )}
      </div>

      <div>
        <div className="wf-card__title">{order.vehicleModel ?? 'Fahrzeug'}</div>
        <div className="wf-card__meta">
          <span className="wf-card__nr">{order.nr ?? order.id}</span>
          {order.customerName ? ` · ${order.customerName}` : ''}
        </div>
      </div>

      {order.taskTotal > 0 ? (
        <progress className="wf-card__progress" value={order.taskDone} max={order.taskTotal} aria-label="Aufgaben-Fortschritt" />
      ) : null}

      <div className="wf-card__facts">
        {order.taskTotal > 0 ? (
          <span className="wf-card__fact">
            <Icon sw={2.2}>
              <path d="M20 6 9 17l-5-5" />
            </Icon>
            {order.taskDone}/{order.taskTotal}
          </span>
        ) : null}
        {order.timeSeconds > 0 ? (
          <span className="wf-card__fact wf-tnum">
            <Icon sw={1.9}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5V12l3 2" />
            </Icon>
            {formatDurationShort(order.timeSeconds)}
          </span>
        ) : null}
        {order.partsCount > 0 ? (
          <span className="wf-card__fact">
            <Icon sw={1.7}>
              <path d="M12 2.5 20 7v10l-8 4.5L4 17V7z" />
              <circle cx="12" cy="12" r="3" />
            </Icon>
            {order.partsCount}
          </span>
        ) : null}
        {order.itemsCount > 0 ? (
          <span className="wf-card__fact">
            <Icon>
              <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.3-.6-.6-2.3z" />
            </Icon>
            {order.itemsCount}
          </span>
        ) : null}
        {showPaid ? (
          <span className={`wf-card__fact ${order.paidAt ? 'wf-card__fact--paid' : 'wf-card__fact--due'}`}>
            {order.paidAt ? '✓ bezahlt' : '€ offen'}
          </span>
        ) : null}
        {overdue ? (
          <span className="wf-card__fact wf-card__fact--overdue">
            <Icon>
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 9h18M8 3v4M16 3v4" />
            </Icon>
            überfällig
          </span>
        ) : schedule ? (
          <span className="wf-card__fact">
            <Icon>
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 9h18M8 3v4M16 3v4" />
            </Icon>
            {schedule}
          </span>
        ) : null}
        {order.customerPhone ? (
          <span className="wf-card__fact wf-card__fact--phone">
            <Icon>
              <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
            </Icon>
            {order.customerPhone}
          </span>
        ) : null}
        <span className="wf-card__mech">
          {order.mechanicName ? (
            <>
              <span className="wf-card__avatar" aria-hidden="true">
                {initials(order.mechanicName)}
              </span>
              {order.mechanicName}
            </>
          ) : (
            <span className="wf-card__fact wf-card__fact--unassigned">
              <Icon sw={2}>
                <path d="M12 9v4M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              </Icon>
              nicht zugewiesen
            </span>
          )}
        </span>
      </div>

      {order.note ? (
        <div className="wf-card__note">
          <Icon sw={1.9}>
            <path d="M12 9v4M12 17h.01" />
            <circle cx="12" cy="12" r="9" />
          </Icon>
          {order.note}
        </div>
      ) : null}
    </Link>
  )
}
